/**
 * Audit explorer, evidence export and per-record timeline — data access.
 *
 * The tenant realm publishes the whole surface (CLAUDE.md §5): `GET /audit`
 * (filtered, cursor-paginated), `/audit/timeline` (one record's history),
 * `/audit/actions` (the server's own closed action vocabulary), `/audit/verify`
 * (hash-chain attestation) and `/audit/export` (evidence CSV). Everything below
 * is a typed call into the generated client, with three pieces of real logic:
 *
 *  - **The action vocabulary is fetched, never declared.** A hardcoded copy of
 *    the server's `audit.Action` enum drifts the first time someone adds an
 *    action; the filter would then quietly offer a value the log cannot hold or
 *    hide one it can. It is cached for the page's lifetime because it cannot
 *    change without a deploy.
 *  - **Business dates become instants in the tenant timezone.** §5 puts business
 *    dates in the tenant's zone; the API takes RFC 3339. A day picked as
 *    "6 August" must mean midnight-to-midnight in Malé, not in UTC.
 *  - **The export is read through a raw `Response`.** Its attestation lives in
 *    headers, which neither the generated client nor an anchor download can see.
 */

import {
  auditLogActions,
  auditLogList,
  auditLogTimeline,
  auditLogVerify,
  type ActionDTO,
  type ChainResult,
  type ExplorerQuery,
  type LogEntry,
  type PageLogEntry,
} from '@erpx/api'

import { api, rawRequest } from '@/api/client'
import { t } from '@/i18n'

export type { ActionDTO, ChainResult, LogEntry, PageLogEntry }

/** Every filter the Explorer and the evidence export share. */
export interface AuditFilters {
  /** Table name, e.g. `users`, `journal_entries`. Exact match server-side. */
  entity: string
  entityId: string
  /** Exact document number — the server compares `doc_no` for equality. */
  docNo: string
  action: string
  /** Numeric id of the acting user, as a string (actors may be non-users). */
  actorId: string
  /** Business date `YYYY-MM-DD`, inclusive, read in the tenant timezone. */
  from: string
  /** Business date `YYYY-MM-DD`, inclusive, read in the tenant timezone. */
  to: string
}

export function emptyAuditFilters(): AuditFilters {
  return { entity: '', entityId: '', docNo: '', action: '', actorId: '', from: '', to: '' }
}

export function isAuditFiltered(filters: AuditFilters): boolean {
  return Object.values(filters).some((value) => value !== '')
}

/* --------------------------------------------------- tenant-timezone day bounds */

/**
 * Offset of `timeZone` from UTC, in milliseconds, at a given instant. Positive
 * means ahead of UTC (Indian/Maldives is +5h).
 *
 * `Intl` formats to whole seconds, so the instant is truncated to seconds
 * before subtracting — otherwise a sub-second remainder would leak into the
 * offset and shift the day boundary by a fraction of a second.
 */
function zoneOffsetMs(instant: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(instant))

  const field = (type: Intl.DateTimeFormatPartTypes): number =>
    Number.parseInt(parts.find((part) => part.type === type)?.value ?? '0', 10)

  const wallClock = Date.UTC(
    field('year'),
    field('month') - 1,
    field('day'),
    field('hour'),
    field('minute'),
    field('second'),
  )
  return wallClock - Math.floor(instant / 1000) * 1000
}

/**
 * The RFC 3339 instant at which `date` reaches `timeOfDay` in `timeZone`.
 *
 * Two passes: the first offset is read at the naive instant, the second at the
 * corrected one, which settles the answer either side of a DST change. Exported
 * for its own test — a filter that silently shifts a day boundary by five hours
 * would quietly exclude evidence.
 */
export function zonedInstant(date: string, timeOfDay: string, timeZone: string): string {
  const naive = Date.parse(`${date}T${timeOfDay}Z`)
  if (Number.isNaN(naive)) return ''

  try {
    const firstPass = naive - zoneOffsetMs(naive, timeZone)
    const settled = naive - zoneOffsetMs(firstPass, timeZone)
    return new Date(settled).toISOString()
  } catch {
    // An unusable zone name must not lose the user their filter; UTC is the
    // boring fallback and the dates are still the ones they typed.
    return new Date(naive).toISOString()
  }
}

/**
 * The filter as wire parameters: only the fields the user actually set, with
 * business dates resolved to instants. One function serves the Explorer, the
 * export and the URL, so the three can never disagree about what is being asked.
 */
export function toFilterParams(filters: AuditFilters, timeZone: string): Record<string, string> {
  const params: Record<string, string> = {}
  if (filters.entity !== '') params['entity'] = filters.entity
  if (filters.entityId !== '') params['entityId'] = filters.entityId
  if (filters.docNo !== '') params['docNo'] = filters.docNo
  if (filters.action !== '') params['action'] = filters.action
  if (filters.actorId !== '') params['actorId'] = filters.actorId
  if (filters.from !== '') params['from'] = zonedInstant(filters.from, '00:00:00', timeZone)
  if (filters.to !== '') params['to'] = zonedInstant(filters.to, '23:59:59', timeZone)
  return params
}

/* ------------------------------------------------------------------- explorer */

export interface AuditPageRequest {
  filters: AuditFilters
  cursor: string
  limit: number
  timeZone: string
}

export function queryAuditLog(request: AuditPageRequest): Promise<PageLogEntry> {
  const params = toFilterParams(request.filters, request.timeZone)
  const query: ExplorerQuery = {}
  if (params['entity'] !== undefined) query.entity = params['entity']
  if (params['entityId'] !== undefined) query.entityId = params['entityId']
  if (params['docNo'] !== undefined) query.docNo = params['docNo']
  if (params['action'] !== undefined) query.action = params['action']
  if (params['actorId'] !== undefined) query.actorId = params['actorId']
  if (params['from'] !== undefined) query.from = params['from']
  if (params['to'] !== undefined) query.to = params['to']
  if (request.cursor !== '') query.cursor = request.cursor
  if (request.limit > 0) query.limit = request.limit
  return auditLogList(api(), query)
}

/** One record's history, newest first — the right rail on every document (§5). */
export function auditTimeline(entity: string, entityId: string, limit = 20): Promise<LogEntry[]> {
  return auditLogTimeline(api(), { entity, entityId, limit })
}

export function verifyAuditChain(): Promise<ChainResult> {
  return auditLogVerify(api())
}

/* ------------------------------------------------------- action vocabulary */

let actionsRequest: Promise<ActionDTO[]> | null = null

/**
 * The server's closed action set with human labels. Cached: the vocabulary is
 * compiled into the binary, so one fetch per page load is one too many already.
 */
export function listAuditActions(): Promise<ActionDTO[]> {
  actionsRequest ??= auditLogActions(api())
    .then((response) => response.actions)
    .catch((error: unknown) => {
      // A failed fetch must not poison the cache — the retry button has to work.
      actionsRequest = null
      throw error
    })
  return actionsRequest
}

/** Test seam: forget the cached vocabulary. */
export function resetAuditActionsCache(): void {
  actionsRequest = null
}

/** Human label for an action code, falling back to the code itself. */
export function actionLabel(code: string, actions: readonly ActionDTO[]): string {
  return actions.find((action) => action.code === code)?.label ?? code
}

/** Colour means state (§11): only the destructive and security actions get one. */
export function actionTone(code: string): 'neutral' | 'success' | 'warning' | 'danger' {
  if (code === 'delete' || code === 'reject' || code === 'reverse' || code === 'login_failure') {
    return 'danger'
  }
  if (code === 'approve' || code === 'post' || code === 'create') return 'success'
  if (
    code === 'export' ||
    code === 'sensitive_reveal' ||
    code === 'permission_change' ||
    code === 'role_change' ||
    code === 'support_session_start'
  ) {
    return 'warning'
  }
  return 'neutral'
}

/** How an actor should be described when no directory name is available. */
export function actorLabel(entry: LogEntry, names: ReadonlyMap<string, string>): string {
  if (entry.actorType === 'system') return t('audit.actors.system')
  if (entry.actorType === 'platform_admin') return t('audit.actors.platformAdmin')
  if (entry.actorType === 'assistant') return t('audit.actors.assistant')
  if (entry.actorId === undefined || entry.actorId === '') return t('audit.actors.unattributed')
  return names.get(entry.actorId) ?? t('audit.actors.userById', { id: entry.actorId })
}

/* --------------------------------------------------------- evidence export */

/**
 * What the server attests to about an export. `rowCount` is `null` when the
 * header did not arrive, which is a different fact from "zero rows" and is
 * reported as such — an auditor handed a bundle with no attestation has been
 * handed an ordinary spreadsheet.
 */
export interface AuditAttestation {
  rowCount: number | null
  chainHash: string
  firstId: string
  lastId: string
  /** False when the response carried no attestation headers at all. */
  present: boolean
}

export interface AuditExport {
  csv: string
  filename: string
  attestation: AuditAttestation
}

const DISPOSITION_FILENAME = /filename="?([^";]+)"?/i

function filenameFrom(disposition: string | null, fallback: string): string {
  const match = disposition === null ? null : DISPOSITION_FILENAME.exec(disposition)
  return match?.[1] ?? fallback
}

function headerCount(raw: string | null): number | null {
  if (raw === null || raw.trim() === '') return null
  const value = Number.parseInt(raw, 10)
  return Number.isFinite(value) ? value : null
}

/**
 * Pull the evidence bundle. Returns the CSV *and* the attestation; the caller
 * decides how to hand the file to the browser, because the attestation has to
 * survive on screen after the download dialog has gone.
 */
export async function exportAuditEvidence(
  filters: AuditFilters,
  timeZone: string,
  fallbackFilename: string,
): Promise<AuditExport> {
  const response = await rawRequest(
    'GET',
    '/api/v1/audit/export',
    toFilterParams(filters, timeZone),
  )

  const csv = await response.text()
  const chainHash = response.headers.get('X-Erpx-Audit-Chain-Hash') ?? ''
  const rowCount = headerCount(response.headers.get('X-Erpx-Audit-Row-Count'))

  return {
    csv,
    filename: filenameFrom(response.headers.get('Content-Disposition'), fallbackFilename),
    attestation: {
      rowCount,
      chainHash,
      firstId: response.headers.get('X-Erpx-Audit-First-Id') ?? '',
      lastId: response.headers.get('X-Erpx-Audit-Last-Id') ?? '',
      present: chainHash !== '' || rowCount !== null,
    },
  }
}
